import changeCase from 'change-case'
import debounce from 'lodash/debounce'
import { array, bool, func, number, object, oneOfType } from 'prop-types'
import React, { Component } from 'react'
import ResizeDetector from 'react-resize-detector'
import VirtualList from 'react-tiny-virtual-list-oss'
import { browserDetection } from '@opallabs/lambda'
import { AnalyticsLayer } from '@opallabs/quanta'
import Loader from '../Loader'
import { kuiperClassNames } from '../utils'
import ColumnHeaders from './ColumnHeaders'
import Row from './Row'
import ConnectedRow from './Row/ConnectedRow'

import './styles.scss'

const loaderRow = (isLazyLoading, style) => (
  <tr key="lazyloader" style={style} aria-hidden="true">
    <td>
      <Loader theme="white" loaded={!isLazyLoading} />
    </td>
  </tr>
)

class Spreadsheet extends Component {
  static propTypes = {
    columns: array,
    columnWidths: oneOfType([array, object]),
    customHeaders: array,
    displayHeader: bool,
    emptyState: func,
    entities: array,
    fetchMoreEntities: func,
    isLazyLoadable: bool,
    isLazyLoading: bool,
    isVirtualized: bool,
    onChangeWidths: func,
    onSortClick: func,
    pageSize: number,
    rowEntityOverride: func,
    rowHeight: number,
    sortParams: object,
  }

  static defaultProps = {
    displayHeader: true,
    entities: [],
    columns: [],
    isVirtualized: false,
    columnWidths: null,
    onChangeWidths: () => {},
  }

  constructor(props) {
    super(props)
    this.state = {
      cellIndex: null,
      cellIsActive: false,
      columnWidths: props.columnWidths || [],
      rowIndex: null,
    }

    this.tableRef = React.createRef()
    this.cellRefsByRow = []
    this.popoverRef = null

    this.debouncedFetchMoreEntities = debounce(
      () =>
        this.props.fetchMoreEntities?.({
          pageSize: this.props.pageSize,
          append: true,
        }),
      500
    )
  }

  // Browsers support e.relatedTarget in different ways on different events
  // Use feature detection to decide which event we need to wire up to
  // the spreadsheet
  needIEHack = () => browserDetection.isIE11()

  activateCell = () => {
    this.setState(() => ({
      cellIsActive: true,
    }))
  }

  deactivateCell = () => {
    this.setState(() => ({
      cellIndex: null,
      cellIsActive: false,
      rowIndex: null,
    }))
  }

  registerPopover = (ref) => {
    this.popoverRef = ref
  }

  unregisterPopover = () => {
    this.popoverRef = null
  }

  onResize = (width, height) => {
    const columnWidths = this.props.columnWidths.map((w) =>
      Math.floor(width * w)
    )
    this.setState({
      columnWidths,
      height,
      width,
    })
  }

  handleCellBlur = (e) => {
    const nextFocusTarget = this.needIEHack()
      ? document.activeElement
      : e.relatedTarget

    const nextFocusInsideSpreadsheet =
      this.tableRef.current.contains(nextFocusTarget)
    const nextFocusSpreadsheetOrPopover =
      nextFocusInsideSpreadsheet || this.nodeIsPopover(nextFocusTarget)

    if (!nextFocusTarget || !nextFocusSpreadsheetOrPopover) {
      this.deactivateCell()
    }
  }

  nodeIsPopover = (node) =>
    node && this.popoverRef && this.popoverRef.current.isEqualNode(node)

  onItemsRendered = (indices) => {
    const { stopIndex } = indices

    const fetchCondition =
      this.props.isLazyLoadable &&
      !this.props.isLazyLoading &&
      stopIndex !== 0 &&
      stopIndex >= this.props.entities.length &&
      stopIndex % this.props.pageSize === 0

    if (fetchCondition) {
      this.debouncedFetchMoreEntities()
    }
  }

  trackList = (listRef) => {
    this.listRef = listRef
  }

  get rows() {
    const {
      displayHeader,
      emptyState: EmptyState,
      entities,
      isVirtualized,
      rowHeight,
    } = this.props
    const { height, width } = this.state
    let rowIndex = 0

    const getAndUpdateRowIndex = () => {
      const indexToReturn = rowIndex
      rowIndex += 1
      return indexToReturn
    }

    if (EmptyState && entities && entities.length === 0) {
      return <EmptyState />
    }

    if (isVirtualized && entities && entities.length) {
      return (
        <VirtualList
          height={displayHeader ? height - rowHeight : height}
          itemCount={entities.length + 1}
          itemSize={rowHeight}
          onItemsRendered={this.onItemsRendered}
          overscanCount={10}
          ref={this.trackList}
          renderItem={this.renderRow.bind(this, [
            ...this.props.entities,
            { rowType: 'loaderRow' },
          ])}
          width={width}
        />
      )
    }

    return entities.map((entity, index) =>
      this.renderRow(entities, {
        index,
        getAndUpdateRowIndex,
      })
    )
  }

  // we need separate implementations of renderRow for virtualized and non-virtualized versions
  renderRow = (entities, { index, style, getAndUpdateRowIndex = () => {} }) => {
    const entity = entities[index]
    let RowType = ConnectedRow

    if (entity.rowType === 'empty' || this.props.rowEntityOverride)
      RowType = Row
    if (entity.rowType === 'loaderRow')
      return loaderRow(this.props.isLazyLoading, style)

    const updatedEntity = this.setFirstAndLast(entity, index)
    const entityKey = entity.key || `${entity.type}-${entity.id}`

    let entityOverride = {}

    if (this.props.rowEntityOverride !== undefined) {
      entityOverride = this.props.rowEntityOverride(entity)
    }

    return (
      <RowType
        {...entityOverride}
        activateCell={this.activateCell}
        activeCellIndex={this.state.cellIndex}
        activeRowIndex={this.state.rowIndex}
        approvalStatus={entity.status}
        cellIsActive={this.state.cellIsActive}
        columns={this.props.columns}
        key={entityKey}
        cellRefsByRow={this.cellRefsByRow}
        columnWidths={this.state.columnWidths}
        entityId={entity.id}
        focusCell={this.focusCell}
        name={entity.name || entity.title}
        entityType={entity.type}
        rowType={entity.rowType}
        rowIndex={getAndUpdateRowIndex()}
        entityIsFirst={updatedEntity.first}
        entityIsLast={updatedEntity.last}
        style={style}
        multiRow={entity.multiRow}
        onPopoverKeyDown={this.handleKeyboardInput}
        registerPopover={this.registerPopover}
        unregisterPopover={this.unregisterPopover}
        handleCellBlur={this.handleCellBlur}
      />
    )
  }

  setFirstAndLast = (entity, index) => {
    const newEntity = entity
    const previousEntity = this.props.entities[index - 1]
    const nextEntity = this.props.entities[index + 1]
    const lastEntityIndex = this.props.entities.length - 1

    const entityIsFirstInGroup =
      previousEntity && previousEntity.rowType === 'group'
    const entityIsLastInGroup =
      (nextEntity && nextEntity.rowType === 'group') ||
      (lastEntityIndex === index && entity.rowType !== 'group')

    if (entityIsFirstInGroup) newEntity.first = true
    if (entityIsLastInGroup) newEntity.last = true

    return newEntity
  }

  get hasNoEntities() {
    const noEntities = this.props.entities.length === 0
    const onlyEntityIsEmpty =
      this.props.entities.length === 1 &&
      this.props.entities[0].rowType === 'empty'

    return noEntities || onlyEntityIsEmpty
  }

  get currentRow() {
    return this.cellRefsByRow[this.state.rowIndex]
  }

  findNextTabbableCellInRow = (rowIndex, startingIndex = -1) =>
    this.cellRefsByRow[rowIndex].findIndex((cell, currentCellIndex) => {
      const noCellRef = !(cell && cell.ref && cell.ref.current)
      if (noCellRef) return false

      const cellIsToRight = currentCellIndex > startingIndex
      const nextCellIsInCurrentRow = cell.tabbable && cellIsToRight

      return nextCellIsInCurrentRow
    })

  findPreviousTabbableCellInRow = (rowIndex, startingCellIndex = -1) => {
    for (
      let cellIndex = startingCellIndex - 1;
      cellIndex > -1;
      cellIndex -= 1
    ) {
      const cell = this.cellRefsByRow[rowIndex][cellIndex]
      const cellRefExists = cell && cell.ref && cell.ref.current

      if (cellRefExists) {
        const cellIsToLeft = cellIndex < startingCellIndex
        const nextCellIsInCurrentRow = cell.tabbable && cellIsToLeft

        if (nextCellIsInCurrentRow) return cellIndex
      }
    }

    return -1
  }

  findNextTabbableCellAfterRow = (startingRowIndex) => {
    let nextCellIndex = -1
    let nextRowIndex = -1

    for (
      let rowIndex = startingRowIndex;
      rowIndex < this.cellRefsByRow.length;
      rowIndex += 1
    ) {
      nextCellIndex = this.findNextTabbableCellInRow(rowIndex)
      const cellFound = nextCellIndex > -1

      if (cellFound) {
        nextRowIndex = rowIndex
        break
      }
    }

    return {
      cellIndex: nextCellIndex,
      rowIndex: nextRowIndex,
    }
  }

  findPreviousTabbableCellBeforeRow = (startingRowIndex) => {
    let nextCellIndex = -1
    let nextRowIndex = -1

    for (let rowIndex = startingRowIndex; rowIndex > -1; rowIndex -= 1) {
      nextCellIndex = this.findPreviousTabbableCellInRow(
        rowIndex,
        this.cellRefsByRow[rowIndex].length
      )
      const cellFound = nextCellIndex > -1

      if (cellFound) {
        nextRowIndex = rowIndex
        break
      }
    }

    return {
      cellIndex: nextCellIndex,
      rowIndex: nextRowIndex,
    }
  }

  tab = (forward = true) => {
    if (this.state.rowIndex < 0 || this.state.cellIndex < 0) return {}

    const activeCell =
      this.cellRefsByRow[this.state.rowIndex][this.state.cellIndex].ref.current

    if (activeCell) activeCell.blur()

    this.setState(
      (state) => {
        let nextCell = {
          cellIndex: state.cellIndex,
          rowIndex: state.rowIndex,
        }

        if (forward) {
          nextCell.cellIndex = this.findNextTabbableCellInRow(
            state.rowIndex,
            state.cellIndex
          )
          const cellNotFound = nextCell.cellIndex < 0

          if (cellNotFound) {
            nextCell = this.findNextTabbableCellAfterRow(state.rowIndex + 1)
          }
        } else {
          nextCell.cellIndex = this.findPreviousTabbableCellInRow(
            state.rowIndex,
            state.cellIndex
          )
          const cellNotFound = nextCell.cellIndex < 0

          if (cellNotFound) {
            nextCell = this.findPreviousTabbableCellBeforeRow(
              state.rowIndex - 1
            )
          }
        }

        return {
          ...nextCell,
          firstCellActive: this.isFirstTabbableCell(nextCell),
          lastCellActive: this.isLastTabbableCell(nextCell),
          cellIsActive: Boolean(
            nextCell.cellIndex > -1 && nextCell.rowIndex > -1
          ),
        }
      },
      () => {
        if (this.state.rowIndex > -1 && this.state.cellIndex > -1) {
          const el =
            this.cellRefsByRow[this.state.rowIndex][
              this.state.cellIndex
            ].ref.current.querySelector('input') ||
            this.cellRefsByRow[this.state.rowIndex][
              this.state.cellIndex
            ].ref.current.querySelector('.kuiper-focusable')
          el.focus()
        }
      }
    )

    return null
  }

  handleKeyboardInput = (e) => {
    const TAB = 9
    const { keyCode, shiftKey, target } = e

    const tagNamesToBypass = ['BUTTON', 'A']

    if (keyCode === TAB && !tagNamesToBypass.includes(target.tagName)) {
      if (shiftKey) {
        if (!this.state.firstCellActive) e.preventDefault()
        this.tab(false)
      } else {
        if (!this.state.lastCellActive) e.preventDefault()
        this.tab()
      }
    }
  }

  isFirstTabbableCell = ({ cellIndex, rowIndex }) =>
    this.firstTabbableIndex.cellIndex === cellIndex &&
    this.firstTabbableIndex.rowIndex === rowIndex

  isLastTabbableCell = ({ cellIndex, rowIndex }) =>
    this.lastTabbableIndex.cellIndex === cellIndex &&
    this.lastTabbableIndex.rowIndex === rowIndex

  focusCell = () => {
    setTimeout(() => {
      this.cellRefsByRow[this.state.rowIndex][this.state.cellIndex].ref.current
        .querySelector('input')
        .focus()
    }, 0)
  }

  get firstTabbableIndex() {
    return this.findNextTabbableCellAfterRow(0)
  }

  get lastTabbableIndex() {
    return this.findPreviousTabbableCellBeforeRow(this.cellRefsByRow.length - 1)
  }

  focusFirstTabbableCell = () => {
    if (this.state.cellIndex < 0 || this.state.rowIndex < 0) {
      this.setState(
        () => ({
          cellIsActive: true,
          cellIndex: this.firstTabbableIndex.cellIndex,
          rowIndex: this.firstTabbableIndex.rowIndex,
        }),
        () => {
          this.cellRefsByRow[this.firstTabbableIndex.rowIndex][
            this.firstTabbableIndex.cellIndex
          ].ref.current.focus()
        }
      )
    }
  }

  focusClickedCell = (e) => {
    let element = e.target

    if (
      changeCase.lowerCase(element.tagName) !== 'input' &&
      !element.classList.contains('kuiper-focusable')
    ) {
      element = element.querySelector('input')
    }

    let cellIndex
    const rowIndex = this.cellRefsByRow.findIndex((row) => {
      const cellFound = row.find((cell, currentColumnIndex) => {
        const elementFound =
          cell && cell.ref.current && cell.ref.current.contains(element)
        if (elementFound) cellIndex = currentColumnIndex
        return elementFound
      })

      return cellFound
    })

    if (rowIndex > -1) {
      this.setState({
        rowIndex,
        cellIndex,
        firstCellActive: this.isFirstTabbableCell({ cellIndex, rowIndex }),
        lastCellActive: this.isLastTabbableCell({ cellIndex, rowIndex }),
        cellIsActive: Boolean(cellIndex > -1 && rowIndex > -1),
      })
    }
  }

  render() {
    const {
      columns,
      customHeaders,
      displayHeader,
      isVirtualized,
      onChangeWidths,
      onSortClick,
      sortParams,
    } = this.props
    return (
      /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
      <AnalyticsLayer name="Spreadsheet">
        <table
          ref={this.tableRef}
          className={kuiperClassNames('spreadsheet', {
            'no-entities': this.hasNoEntities,
            isVirtualized,
          })}
          onFocus={this.focusFirstTabbableCell}
          onClickCapture={this.focusClickedCell}
          onKeyDown={this.handleKeyboardInput}
        >
          <thead>
            {displayHeader && (
              <ColumnHeaders
                columns={columns}
                columnWidths={this.state.columnWidths}
                customHeaders={customHeaders}
                onChangeWidths={onChangeWidths}
                onSortClick={onSortClick}
                sortParams={sortParams}
              />
            )}
          </thead>
          <tbody>{this.rows}</tbody>
        </table>
        {isVirtualized && (
          <ResizeDetector
            handleWidth={true}
            handleHeight={true}
            onResize={this.onResize}
          />
        )}
      </AnalyticsLayer>
    )
  }
}

export default Spreadsheet
